import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  RefreshCw, Play, Loader2, CheckCircle2, AlertTriangle,
  Database, Users, MapPin, Link2, History, ArrowRight,
  ShieldCheck, Clock, FileText,
} from "lucide-react";

const SCOPES = [
  { id: "all",          label: "Tout",           hint: "Chauffeurs + Affectations (POI exclus)", Icon: Database },
  { id: "drivers",      label: "Chauffeurs",     hint: "Employees Logitrak",                Icon: Users },
  { id: "pois",         label: "POI",            hint: "Désactivé — POI gérés dans LOGIBUS (POI MBC)", Icon: MapPin, disabled: true },
  { id: "affectations", label: "Affectations",   hint: "Chauffeur ↔ Tracker",             Icon: Link2 },
];

export default function LogitrakSync() {
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scope, setScope] = useState("all");
  const [running, setRunning] = useState(false);
  const [preview, setPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get("/navixy-sync/status"),
        api.get("/navixy-sync/logs?limit=20"),
      ]);
      setStatus(s.data);
      setLogs(l.data?.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const runDryRun = async () => {
    setRunning(true); setPreview(null);
    try {
      const { data } = await api.post("/navixy-sync/run", { scope, dry_run: true });
      setPreview(data);
      setShowPreview(true);
      toast.success("Simulation terminée — Vérifiez le rapport avant d'appliquer");
      await loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setRunning(false);
    }
  };

  const runReal = async () => {
    if (!preview) return;
    setRunning(true);
    try {
      const { data } = await api.post("/navixy-sync/run", { scope, dry_run: false });
      toast.success(`Synchronisation OK — ${JSON.stringify(data.written)}`);
      setPreview(null);
      setShowPreview(false);
      await loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setRunning(false);
    }
  };

  const retryFailed = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/navixy-sync/retry-failed");
      const d = data.drivers || {}, p = data.pois || {};
      toast.success(
        `Drivers retried ${d.retried || 0} (${d.ok || 0} OK / ${d.ko || 0} KO) · POIs retried ${p.retried || 0} (${p.ok || 0} OK / ${p.ko || 0} KO)`
      );
      await loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setRunning(false);
    }
  };

  if (loading && !status) {
    return (
      <div className="p-6 flex items-center gap-2 text-zinc-500 text-sm" data-testid="navixy-sync-loading">
        <Loader2 className="animate-spin" size={14} /> Chargement…
      </div>
    );
  }

  const stats = status?.stats || {};
  const lastReal = status?.last_real_sync;
  const lastAny = status?.last_log;

  return (
    <div className="p-4 lg:p-6" data-testid="navixy-sync-page">
      {/* Header */}
      <header className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Intégration · Logitrak</div>
          <h1 className="font-display font-black uppercase text-3xl lg:text-4xl tracking-tighter">Synchronisation Logitrak</h1>
          <p className="text-sm text-zinc-600 mt-1">
            Importez automatiquement vos POI, chauffeurs et affectations depuis Logitrak / Logitrak.
          </p>
        </div>
        <button onClick={loadAll} disabled={loading}
          data-testid="navixy-refresh"
          className="inline-flex items-center gap-2 border border-zinc-900 px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-900 hover:text-white disabled:opacity-50">
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Rafraîchir
        </button>
      </header>

      {/* Configured / not configured banner */}
      <div className={`border border-zinc-900 p-4 mb-6 flex items-center gap-3 ${status?.configured ? "bg-emerald-50" : "bg-amber-50"}`}
           data-testid="navixy-config-banner">
        {status?.configured
          ? <><CheckCircle2 size={18} className="text-emerald-700" /> <span className="text-sm">Clé API Logitrak configurée. Synchronisations automatiques toutes les 15 minutes.</span></>
          : <><AlertTriangle size={18} className="text-amber-700" /> <span className="text-sm">Aucune clé API Logitrak détectée. Configurez-la dans <strong>Paramètres → Logitrak</strong>.</span></>
        }
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-0 border border-zinc-900 mb-6" data-testid="navixy-stats">
        <Stat label="POI synchronisés" value={`${stats.pois_active || 0}/${stats.pois_total || 0}`} hint="Actifs / total" Icon={MapPin} />
        <Stat label="Chauffeurs synchronisés" value={`${stats.drivers_synced || 0}/${stats.drivers_total || 0}`} hint="Logitrak / Logibus" Icon={Users} />
        <Stat label="Affectations synchronisées" value={stats.affectations_synced || 0} hint="Tracker + Chauffeur" Icon={Link2} />
        <Stat label="Dernière sync réelle" value={lastReal ? new Date(lastReal.started_at).toLocaleString("fr-CH") : "—"} hint={lastReal?.triggered_by || "Jamais"} Icon={ShieldCheck} />
        <Stat label="Dernier événement" value={lastAny ? new Date(lastAny.started_at).toLocaleString("fr-CH") : "—"} hint={lastAny?.dry_run ? "Dry-run" : (lastAny?.triggered_by || "—")} Icon={Clock} last />
      </div>

      {/* Action panel */}
      <section className="border border-zinc-900 bg-white mb-6" data-testid="navixy-action-panel">
        <header className="px-4 py-3 border-b border-zinc-900 flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-base">Lancer une synchronisation</div>
            <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">Dry-run d'abord, application sur confirmation</div>
          </div>
        </header>
        <div className="p-4 flex flex-col gap-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border border-zinc-300" data-testid="navixy-scope-selector">
            {SCOPES.map(({ id, label, hint, Icon, disabled }) => (
              <button key={id} onClick={() => !disabled && setScope(id)} data-testid={`scope-${id}`} disabled={disabled}
                title={disabled ? "Synchronisation Navixy → POI désactivée" : undefined}
                className={`p-3 border-r border-zinc-300 last:border-r-0 text-left transition ${disabled ? "bg-zinc-100 text-zinc-400 cursor-not-allowed line-through" : scope === id ? "bg-[#FFC60A]" : "bg-white hover:bg-zinc-50"}`}>
                <div className="flex items-center gap-2 mb-1"><Icon size={14} /> <span className="text-[11px] font-bold uppercase tracking-widest">{label}</span></div>
                <div className="text-[10px] font-mono text-zinc-600">{hint}</div>
              </button>
            ))}
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={runDryRun} disabled={running || !status?.configured}
              data-testid="navixy-dryrun-btn"
              className="inline-flex items-center gap-2 border border-zinc-900 bg-zinc-900 text-white px-4 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-zinc-800 disabled:opacity-50">
              {running ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
              Simuler (dry-run)
            </button>
            <button onClick={runReal}
              disabled={running || !preview || preview.dry_run === false}
              data-testid="navixy-apply-btn"
              className="inline-flex items-center gap-2 border border-zinc-900 bg-[#FFC60A] text-zinc-900 px-4 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-yellow-300 disabled:opacity-50">
              <ArrowRight size={12} /> Appliquer la sync
            </button>
            <button onClick={retryFailed} disabled={running}
              data-testid="navixy-retry-failed-btn"
              className="inline-flex items-center gap-2 border border-zinc-900 bg-white px-4 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-orange-50 disabled:opacity-50">
              <RefreshCw size={12} /> Réessayer les erreurs
            </button>
            {preview && (
              <button onClick={() => { setPreview(null); setShowPreview(false); }} data-testid="navixy-clear-btn"
                className="inline-flex items-center gap-2 border border-zinc-900 bg-white px-4 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-zinc-100">
                Annuler
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Dry-run preview */}
      {showPreview && preview && (
        <section className="border border-zinc-900 bg-white mb-6" data-testid="navixy-preview-panel">
          <header className="px-4 py-3 border-b border-zinc-900 flex items-center justify-between">
            <div>
              <div className="font-display font-bold text-base flex items-center gap-2">
                <FileText size={14} /> Rapport de simulation (dry-run)
              </div>
              <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">
                Source : {preview.source_totals?.trackers} trackers · {preview.source_totals?.employees} employés · {preview.source_totals?.pois} POI
              </div>
            </div>
            <span className="text-[10px] font-mono px-2 py-1 border border-zinc-900 bg-amber-50">DRY-RUN</span>
          </header>
          <div className="p-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              <DiffCount label="POI à créer"      value={preview.counts.pois_create}      color="bg-emerald-50 border-emerald-700" />
              <DiffCount label="POI à mettre à jour" value={preview.counts.pois_update}   color="bg-blue-50 border-blue-700" />
              <DiffCount label="Chauffeurs à créer" value={preview.counts.drivers_create} color="bg-emerald-50 border-emerald-700" />
              <DiffCount label="Chauffeurs à MAJ"  value={preview.counts.drivers_update}  color="bg-blue-50 border-blue-700" />
              <DiffCount label="Affectations à MAJ" value={preview.counts.affectations_update} color="bg-blue-50 border-blue-700" />
              <DiffCount label="Trackers à re-mapper" value={preview.counts.vehicle_tracker_remap || 0} color="bg-purple-50 border-purple-700" />
              <DiffCount label="POI inchangés"     value={preview.counts.pois_unchanged}  color="bg-zinc-50 border-zinc-400" />
              <DiffCount label="Chauffeurs inchangés" value={preview.counts.drivers_unchanged} color="bg-zinc-50 border-zinc-400" />
              <DiffCount label="POI absents de Logitrak" value={preview.counts.pois_removed_from_source} color="bg-orange-50 border-orange-700" />
            </div>

            {(preview.preview?.vehicle_tracker_remap || []).length > 0 && (
              <PreviewTable
                title={`Trackers à re-mapper (${preview.preview.vehicle_tracker_remap.length})`}
                rows={preview.preview.vehicle_tracker_remap.slice(0, 10)}
                columns={[
                  ["Plaque", "plate"],
                  ["Véhicule", "vehicle_model"],
                  ["Tracker ancien", "old_tracker_id"],
                  ["Tracker nouveau", "new_tracker_id"],
                  ["Label Logitrak", "label"],
                ]}
                testid="preview-tracker-remap"
              />
            )}
            {(preview.preview?.affectations_update || []).length > 0 && (
              <PreviewTable
                title={`Affectations à mettre à jour (${preview.preview.affectations_update.length})`}
                rows={preview.preview.affectations_update.slice(0, 10)}
                columns={[
                  ["Plaque", "vehicle_plate"],
                  ["Chauffeur", "driver_name"],
                  ["Tracker", "tracker_id"],
                  ["Logitrak emp id", "navixy_employee_id"],
                ]}
                testid="preview-affectations"
              />
            )}

            {(preview.preview?.drivers_create || []).length > 0 && (
              <PreviewTable
                title={`Chauffeurs à créer (${preview.preview.drivers_create.length})`}
                rows={preview.preview.drivers_create.slice(0, 5)}
                columns={[
                  ["Nom", "name"],
                  ["Email", "email"],
                  ["Tél", "phone"],
                  ["Logitrak ID", "navixy_employee_id"],
                  ["Tracker", "navixy_tracker_id"],
                ]}
                testid="preview-drivers-create"
              />
            )}
            {(preview.preview?.pois_create || []).length > 0 && (
              <PreviewTable
                title={`POI à créer (${preview.preview.pois_create.length})`}
                rows={preview.preview.pois_create.slice(0, 5)}
                columns={[
                  ["Label", "label"],
                  ["Adresse", "address"],
                  ["Lat", "lat"],
                  ["Lng", "lng"],
                  ["Rayon (m)", "radius"],
                ]}
                testid="preview-pois-create"
              />
            )}
            {(preview.preview?.affectations_update || []).length > 0 && (
              <PreviewTable
                title={`Affectations à mettre à jour (${preview.preview.affectations_update.length})`}
                rows={preview.preview.affectations_update.slice(0, 8)}
                columns={[
                  ["Véhicule", "vehicle_plate"],
                  ["Tracker", "tracker_id"],
                  ["Chauffeur", "driver_name"],
                  ["Logitrak Emp.", "navixy_employee_id"],
                ]}
                testid="preview-affectations"
              />
            )}
            {preview.errors?.length > 0 && (
              <div className="border border-red-700 bg-red-50 p-3 text-xs font-mono">
                <div className="font-bold mb-1">Erreurs :</div>
                {preview.errors.map((e, i) => <div key={i}>· {e}</div>)}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Journal */}
      <section className="border border-zinc-900 bg-white" data-testid="navixy-journal">
        <header className="px-4 py-3 border-b border-zinc-900 flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-base flex items-center gap-2">
              <History size={14} /> Journal des synchronisations
            </div>
            <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">
              {logs.length} dernier(s) événement(s)
            </div>
          </div>
        </header>
        {logs.length === 0 ? (
          <div className="p-6 text-center text-xs font-mono text-zinc-500 italic">
            Aucune synchronisation enregistrée pour l'instant.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-zinc-900 text-[#FFC60A]">
                <tr>
                  <th className="px-3 py-2 text-left font-bold uppercase tracking-widest">Date</th>
                  <th className="px-3 py-2 text-left font-bold uppercase tracking-widest">Source</th>
                  <th className="px-3 py-2 text-left font-bold uppercase tracking-widest">Scope</th>
                  <th className="px-3 py-2 text-left font-bold uppercase tracking-widest">Mode</th>
                  <th className="px-3 py-2 text-right font-bold uppercase tracking-widest">POI</th>
                  <th className="px-3 py-2 text-right font-bold uppercase tracking-widest">Drivers</th>
                  <th className="px-3 py-2 text-right font-bold uppercase tracking-widest">Affec.</th>
                  <th className="px-3 py-2 text-left font-bold uppercase tracking-widest">Statut</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} className="border-t border-zinc-200" data-testid={`log-row-${l.id}`}>
                    <td className="px-3 py-2 font-mono text-[11px]">{new Date(l.started_at).toLocaleString("fr-CH")}</td>
                    <td className="px-3 py-2">{l.triggered_by}</td>
                    <td className="px-3 py-2 font-mono">{l.scope}</td>
                    <td className="px-3 py-2">
                      {l.dry_run
                        ? <span className="px-1.5 py-0.5 bg-amber-100 border border-amber-700 text-[10px] font-mono">dry-run</span>
                        : <span className="px-1.5 py-0.5 bg-emerald-100 border border-emerald-700 text-[10px] font-mono">applied</span>}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {l.dry_run ? `+${l.counts?.pois_create || 0}` : `+${l.written?.pois_created || 0}`}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {l.dry_run ? `+${l.counts?.drivers_create || 0}` : `+${l.written?.drivers_created || 0}`}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {l.dry_run ? `${l.counts?.affectations_update || 0}` : `${l.written?.affectations_updated || 0}`}
                    </td>
                    <td className="px-3 py-2">
                      {l.status === "ok"
                        ? <span className="text-emerald-700">✓ OK</span>
                        : l.status === "error"
                        ? <span className="text-red-700">✕ {l.error?.slice(0, 40)}</span>
                        : <span className="text-amber-700">⚠ Partiel</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value, hint, Icon, last = false }) {
  return (
    <div className={`p-4 border-r border-zinc-900 ${last ? "border-r-0" : ""}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">{label}</div>
        <Icon size={14} className="text-zinc-500" />
      </div>
      <div className="font-display font-black text-xl lg:text-2xl tracking-tighter">{value}</div>
      <div className="text-[10px] font-mono text-zinc-500 mt-0.5 truncate">{hint}</div>
    </div>
  );
}

function DiffCount({ label, value, color }) {
  return (
    <div className={`border px-3 py-2 ${color}`} data-testid={`diff-${label.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/gi, '')}`}>
      <div className="text-[10px] font-mono uppercase tracking-widest opacity-70">{label}</div>
      <div className="font-display font-black text-2xl tracking-tighter">{value || 0}</div>
    </div>
  );
}

function PreviewTable({ title, rows, columns, testid }) {
  if (!rows?.length) return null;
  return (
    <div className="mb-4 border border-zinc-300" data-testid={testid}>
      <div className="bg-zinc-100 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest">{title}</div>
      <table className="w-full text-xs">
        <thead className="bg-zinc-50 border-b border-zinc-300">
          <tr>{columns.map(([lbl]) => (
            <th key={lbl} className="px-3 py-1.5 text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500">{lbl}</th>
          ))}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-zinc-200">
              {columns.map(([lbl, key]) => (
                <td key={lbl} className="px-3 py-1.5 font-mono text-[11px]">
                  {r[key] != null ? String(r[key]) : "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
